import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, Crown, CreditCard, BarChart3, Bot, Flag, LifeBuoy,
  Megaphone, Activity, ScrollText, ShieldCheck, Settings as Cog, LogOut, Menu, X,
  Lightbulb, MessageSquare,
} from "lucide-react";

import { useAuth } from "@/context/AuthContext";
import { adminService } from "@/services/adminService";
import { ASSETS } from "@/config/brand";

const NAV = [
  { section: "Overview", items: [["/admin", "Command Center", LayoutDashboard]] },
  { section: "Customers", items: [["/admin/customers", "Customers", Users], ["/admin/founding", "Founding Agents", Crown], ["/admin/subscriptions", "Subscriptions", CreditCard]] },
  { section: "Product", items: [["/admin/analytics", "Product Analytics", BarChart3], ["/admin/ai", "AI Ops", Bot], ["/admin/flags", "Feature Flags", Flag]] },
  { section: "Operations", items: [["/admin/support", "Support", LifeBuoy], ["/admin/feature-requests", "Feature Requests", Lightbulb], ["/admin/feedback", "Feedback", MessageSquare], ["/admin/announcements", "Announcements", Megaphone]] },
  { section: "System", items: [["/admin/health", "Platform Health", Activity], ["/admin/audit", "Audit Logs", ScrollText], ["/admin/security", "Security", ShieldCheck], ["/admin/settings", "Settings", Cog]] },
];

export const AdminShell = ({ children, title, description }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [ctx, setCtx] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => { adminService.context().then(setCtx).catch(() => setCtx(null)); }, []);
  const isSuper = ctx?.is_super_admin;

  return (
    <div className="flex min-h-screen bg-brand-light-gray" data-testid="admin-shell">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-40 w-64 transform bg-brand-navy text-white transition-transform lg:static lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="flex h-16 items-center gap-2.5 border-b border-white/10 px-5">
          <img src={ASSETS.mark} alt="RESIDENNCE" className="h-8 w-8 shrink-0 object-contain" />
          <div><p className="font-brand text-sm font-bold leading-none">RESIDENNCE</p><p className="text-[10px] uppercase tracking-widest text-brand-soft-gold">Admin</p></div>
          <button className="ml-auto lg:hidden" onClick={() => setOpen(false)}><X className="h-5 w-5" /></button>
        </div>
        <nav className="space-y-5 overflow-y-auto px-3 py-4" style={{ maxHeight: "calc(100vh - 4rem)" }}>
          {NAV.map((grp) => (
            <div key={grp.section}>
              <p className="px-2 pb-1 text-[10px] font-bold uppercase tracking-widest text-white/40">{grp.section}</p>
              {grp.items.map(([to, label, Icon]) => (
                <NavLink key={to} to={to} end={to === "/admin"} onClick={() => setOpen(false)} data-testid={`admin-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) => `flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm transition-colors ${isActive ? "bg-white/10 font-semibold text-white" : "text-white/70 hover:bg-white/5 hover:text-white"}`}>
                  <Icon className="h-4 w-4" /> {label}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
      </aside>
      {open && <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-16 items-center gap-3 border-b border-border bg-white px-4 lg:px-8">
          <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="admin-menu"><Menu className="h-5 w-5" /></button>
          <div className="min-w-0 flex-1">
            <h1 className="truncate font-brand text-base font-bold text-brand-navy">{title}</h1>
            {description && <p className="truncate text-xs text-muted-foreground">{description}</p>}
          </div>
          <span className="hidden text-xs text-muted-foreground sm:inline">{user?.email} · {isSuper ? "Super Admin" : "Support Admin"}</span>
          <button onClick={() => navigate("/")} className="rounded-md px-2 py-1 text-xs font-medium text-accent hover:underline" data-testid="admin-exit">Exit to app</button>
          <button onClick={logout} className="rounded-md p-2 text-muted-foreground hover:bg-secondary" data-testid="admin-logout"><LogOut className="h-4 w-4" /></button>
        </header>
        <main className="flex-1 overflow-x-hidden p-4 lg:p-8" data-testid="admin-content">{children}</main>
      </div>
    </div>
  );
};

export const StatCard = ({ label, value, sub, accent }) => (
  <div className="rounded-xl border border-border bg-white p-4" data-testid="admin-stat">
    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className={`mt-1 font-brand text-2xl font-bold ${accent ? "text-brand-navy" : "text-brand-charcoal"}`}>{value}</p>
    {sub && <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p>}
  </div>
);
